import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';

import { widget as Widget } from '../../charting_library';
import {
  IChartingLibraryWidget,
  AvailableSaveloadVersions,
  StudyOverrides,
} from '../../charting_library/charting_library';
import { ResolutionString } from '../../charting_library/datafeed-api.d';
import useMarkets from '../../contexts/MarketsContext';
import { useDatafeed } from '../../hooks/useDataFeed';

const Chart = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
`;

interface AdvancedChartProps {
  symbol?: string;
  interval?: ResolutionString;
  datafeedUrl?: string;
  libraryPath?: string;
  chartsStorageUrl?: string;
  chartsStorageApiVersion?: AvailableSaveloadVersions;
  clientId?: string;
  userId?: string;
  fullscreen?: boolean;
  autosize?: boolean;
  studiesOverrides?: StudyOverrides;
}

export function AdvancedChart({
  symbol,
  datafeedUrl,
  interval,
  libraryPath,
  chartsStorageUrl,
  chartsStorageApiVersion,
  clientId,
  userId,
  fullscreen,
  autosize,
  studiesOverrides,
}: AdvancedChartProps) {
  const chartRef = useRef(null);
  const [chartWidget, setChartWidget] = useState<IChartingLibraryWidget>();

  const { selectedMarketId } = useMarkets();
  const dataFeed = useDatafeed();

  useEffect(() => {
    if (chartRef.current && !chartWidget && dataFeed) {
      const tvWidget = new Widget({
        symbol,
        // BEWARE: no trailing slash is expected in feed URL
        datafeed: dataFeed,
        interval: interval || ('60' as ResolutionString),
        container: chartRef.current,
        library_path: libraryPath,
        theme: 'Dark',
        locale: 'en',
        disabled_features: [
          'control_bar',
          'use_localstorage_for_settings',
          'header_symbol_search',
          'volume_force_overlay',
        ],
        charts_storage_url: chartsStorageUrl,
        charts_storage_api_version: chartsStorageApiVersion,
        client_id: clientId,
        user_id: userId,
        fullscreen,
        autosize,
        studies_overrides: studiesOverrides || {},
      });
      tvWidget.onChartReady(() => {
        // load autosave settings from localstorage
        const storedData = localStorage.getItem('tv_chart_state');
        if (storedData) {
          tvWidget?.load(JSON.parse(storedData));
        }
        // autosave chart
        tvWidget.subscribe('onAutoSaveNeeded', () => {
          tvWidget?.save((data) => {
            localStorage.setItem('tv_chart_state', JSON.stringify(data));
          });
        });
      });
      setChartWidget(tvWidget);
    }
  }, [
    chartRef,
    chartWidget,
    symbol,
    datafeedUrl,
    interval,
    libraryPath,
    chartsStorageUrl,
    chartsStorageApiVersion,
    clientId,
    userId,
    fullscreen,
    autosize,
    studiesOverrides,
    dataFeed,
  ]);

  useEffect(() => {
    if (selectedMarketId !== undefined && chartWidget !== undefined) {
      try {
        chartWidget.onChartReady(() => {
          chartWidget.setSymbol?.(
            selectedMarketId,
            interval ?? ('60' as ResolutionString),
            () => null,
          );
        });
      } catch (err) {
        console.warn(err);
      }
    }
  }, [selectedMarketId, chartWidget, interval]);

  return <Chart ref={chartRef} />;
}

AdvancedChart.defaultProps = {
  symbol: '0',
  interval: '60',
  datafeedUrl: 'https://demo_feed.tradingview.com',
  libraryPath: '/charting_library/',
  chartsStorageUrl: 'https://saveload.tradingview.com',
  chartsStorageApiVersion: '1.1',
  clientId: 'tradingview.com',
  userId: 'public_user_id',
  fullscreen: false,
  autosize: true,
  studiesOverrides: {},
};
